export type OS = "macos" | "windows" | "linux";
export const OS_LIST: readonly OS[] = ["windows", "macos", "linux"];
export const OS_LABEL: Record<OS, string> = { windows: "Windows", macos: "macOS", linux: "Linux" };

/** The parts of `navigator` we read; a plain object in tests. */
export interface NavLike {
  userAgentData?: { platform?: string };
  platform?: string;
  userAgent?: string;
}

/**
 * Best guess at the computer the reader is sitting at, or null when we cannot tell (a phone, a
 * tablet, ChromeOS). It is only a DEFAULT: the browser's OS is not always the terminal's (WSL, a
 * remote machine), so the page always shows the switch.
 */
export function detectOs(nav: NavLike | undefined): OS | null {
  if (!nav) return null;
  const ua = nav.userAgent ?? "";
  if (/Android|iPhone|iPad|iPod|CrOS/i.test(ua)) return null;
  // Chromium reports it directly; everything else falls back to the older fields.
  const hint = `${nav.userAgentData?.platform ?? ""} ${nav.platform ?? ""}`.toLowerCase();
  if (/mac/.test(hint)) return "macos";
  if (/win/.test(hint)) return "windows";
  if (/linux|x11|ubuntu|debian|fedora/.test(hint)) return "linux";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux|X11/i.test(ua)) return "linux";
  return null;
}

const KEY = "setup-os";

export function rememberedOs(storage: Pick<Storage, "getItem"> | undefined): OS | null {
  try {
    const v = storage?.getItem(KEY);
    return v === "macos" || v === "windows" || v === "linux" ? v : null;
  } catch {
    return null; // storage can throw (private mode, blocked cookies); the default still works
  }
}

export function rememberOs(storage: Pick<Storage, "setItem"> | undefined, os: OS) {
  try {
    storage?.setItem(KEY, os);
  } catch {
    /* not remembering is harmless */
  }
}
